import { AimOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { Button, Drawer, Input, message, Modal, Select, Table } from 'antd';
import React, { useEffect, useState } from 'react';
import { getProductId } from '@/infrastructure/product-info';
import { DomainBelongType } from '@/proto/manage';
import { domainManageService } from '@/service/domain-manage';
import { netHttpService } from '@/service/net-http';
import styles from './domain-ownership.module.scss';

export const OWNERSHIP_LABEL = {
  [DomainBelongType.DOMAIN_BELONG_ALL]: '全部',
  [DomainBelongType.DOMAIN_BELONG_SELF]: '自身',
  [DomainBelongType.DOMAIN_BELONG_THIRD]: '第三方',
  [DomainBelongType.DOMAIN_BELONG_UNCLASSIFIED]: '未归类',
};

export const OWNERSHIP_COLOR = {
  [DomainBelongType.DOMAIN_BELONG_ALL]: { color: '#1890ff', bg: '#e6f7ff' },
  [DomainBelongType.DOMAIN_BELONG_SELF]: { color: '#52c41a', bg: '#f6ffed' },
  [DomainBelongType.DOMAIN_BELONG_THIRD]: { color: '#faad14', bg: '#fffbe6' },
  [DomainBelongType.DOMAIN_BELONG_UNCLASSIFIED]: { color: '#8c8c8c', bg: '#fafafa' },
};

const OWNERSHIP_TYPES = [
  DomainBelongType.DOMAIN_BELONG_SELF,
  DomainBelongType.DOMAIN_BELONG_THIRD,
  DomainBelongType.DOMAIN_BELONG_UNCLASSIFIED,
] as const;

interface DomainRecord {
  id: string;
  domain: string;
  count: number;
  ownership: number;
}

/** 归属类型选择器（弹窗和表格列共用） */
interface IOwnershipSelectorProps {
  value: number;
  onChange: (value: number) => void;
}
const OwnershipSelector: React.FC<IOwnershipSelectorProps> = (props) => {
  const { value, onChange } = props;
  return (
    <div className={styles.ownershipSelector}>
      {OWNERSHIP_TYPES.map((type) => {
        const isActive = value === type;
        const { color } = OWNERSHIP_COLOR[type];
        return (
          <button
            key={type}
            type="button"
            className={styles.ownershipSelectorItem}
            style={{
              color: isActive ? color : undefined,
              fontWeight: isActive ? 600 : undefined,
              backgroundColor: isActive ? 'rgba(255, 255, 255, 0.8)' : undefined,
            }}
            onClick={() => onChange(type)}
          >
            {OWNERSHIP_LABEL[type]}
          </button>
        );
      })}
    </div>
  );
};

interface IDomainOwnershipBatchAdjusterProps {
  selectedRows: { host: string }[];
  refresh: () => void;
}

export const DomainOwnershipBatchAdjuster: React.FC<IDomainOwnershipBatchAdjusterProps> = ({
  selectedRows,
  refresh,
}) => {
  const handleBatchChange = async (belong: number) => {
    try {
      await domainManageService.upsertDomainTags({
        productId: getProductId(),
        items: selectedRows.map(r => ({ domain: r.host, belong })),
      });
      message.success(`已将 ${selectedRows.length} 个域名调整为 [${OWNERSHIP_LABEL[belong as keyof typeof OWNERSHIP_LABEL]}]`);
      refresh();
    } catch (e) {
      message.error('修改失败');
    }
  };

  return (
    <div className={styles.batchAdjuster}>
      调整归属：
      {OWNERSHIP_TYPES.map((type) => {
        const dotStyle: Record<string, string> = { '--dot-color': OWNERSHIP_COLOR[type].color };
        return (
          <button
            key={type}
            type="button"
            className={styles.batchAdjusterItem}
            style={dotStyle}
            onClick={() => handleBatchChange(type)}
          >
            <span className={styles.batchAdjusterDot} />
            {OWNERSHIP_LABEL[type]}
          </button>
        );
      })}
    </div>
  );
};

/** 域名列表「域名归属」列下拉选择器：人人可改 */
interface IDomainOwnershipSelectorProps {
  domain: string;
  value: number;
  refresh: () => void;
}

export const DomainOwnershipSelector: React.FC<IDomainOwnershipSelectorProps> = (props) => {
  const { domain, value, refresh } = props;
  const [current, setCurrent] = useState<number>(value);

  useEffect(() => {
    setCurrent(value);
  }, [value]);

  const handleChange = async (belong: number) => {
    setCurrent(belong);
    try {
      await domainManageService.upsertDomainTags({
        productId: getProductId(),
        items: [{ domain, belong }],
      });
      refresh();
      message.success('修改成功');
    } catch (e) {
      message.error('修改失败');
    }
  };

  return (
    <Select
      value={current}
      style={{ width: '100%' }}
      placeholder={'请选择'}
      onChange={handleChange}
      options={OWNERSHIP_TYPES.map(type => ({
        value: type,
        label: OWNERSHIP_LABEL[type],
      }))}
    />
  );
};

/** 新增域名弹窗 */
interface IAddDomainModalProps {
  visible: boolean;
  onClose: () => void;
  onOk: (domain: string, belong: number) => void;
}
const AddDomainModal: React.FC<IAddDomainModalProps> = (props) => {
  const { visible, onClose, onOk } = props;
  const [domain, setDomain] = useState('');
  const [belong, setBelong] = useState<number>(DomainBelongType.DOMAIN_BELONG_SELF);

  const handleOk = () => {
    if (!domain.trim()) {
      message.warning('请输入域名');
      return;
    }
    onOk(domain.trim(), belong);
    setDomain('');
    setBelong(DomainBelongType.DOMAIN_BELONG_SELF);
    onClose();
  };

  return (
    <Modal
      title="新增域名"
      visible={visible}
      onCancel={onClose}
      onOk={handleOk}
      okText="添加"
      cancelText="取消"
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 6 }}>域名</div>
        <Input
          placeholder="例如：api.example.com"
          value={domain}
          onChange={e => setDomain(e.target.value)}
          onPressEnter={handleOk}
        />
      </div>
      <div>
        <div style={{ marginBottom: 6 }}>归属类型</div>
        <OwnershipSelector value={belong} onChange={setBelong} />
      </div>
    </Modal>
  );
};

interface DomainOwnershipDrawerProps {
  visible: boolean;
  onClose: () => void;
  refreshExternalTable: () => void;
}

const DomainOwnershipDrawer: React.FC<DomainOwnershipDrawerProps> = (props) => {
  const { visible, onClose, refreshExternalTable } = props;
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchText, setSearchText] = useState('');
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(String(DomainBelongType.DOMAIN_BELONG_ALL));

  const { data, loading, refresh: refreshInternalTable } = useRequest(
    async () => {
      const rsp = await netHttpService.getDomainStats({ productId: getProductId() });
      const items = rsp?.items || [];
      return items.map(item => ({
        id: item.domain ?? '',
        domain: item.domain ?? '',
        count: Number(item.docCount) || 0,
        ownership: (item.belong as number) ?? DomainBelongType.DOMAIN_BELONG_UNCLASSIFIED,
      }));
    },
    {
      ready: visible,
      refreshDeps: [visible],
    },
  );

  const refreshTable = () => {
    refreshInternalTable();
    refreshExternalTable();
  };

  /** 统计各归属类型数量 */
  const safeData = data || [];
  const stats = {
    all: safeData.length,
    self: safeData.filter(d => d.ownership === DomainBelongType.DOMAIN_BELONG_SELF).length,
    third: safeData.filter(d => d.ownership === DomainBelongType.DOMAIN_BELONG_THIRD).length,
    unclassified: safeData.filter(d => d.ownership === DomainBelongType.DOMAIN_BELONG_UNCLASSIFIED).length,
  } as const;

  const statsMap: Record<number, number> = {
    [DomainBelongType.DOMAIN_BELONG_ALL]: stats.all,
    [DomainBelongType.DOMAIN_BELONG_SELF]: stats.self,
    [DomainBelongType.DOMAIN_BELONG_THIRD]: stats.third,
    [DomainBelongType.DOMAIN_BELONG_UNCLASSIFIED]: stats.unclassified,
  };


  const displayData = safeData as DomainRecord[];

  /** 单行修改归属 */
  const changeOwnership = async (id: string, ownership: number) => {
    try {
      await domainManageService.upsertDomainTags({
        productId: getProductId(),
        items: [{ domain: id, belong: ownership }],
      });
      message.success('修改成功');
      refreshTable();
    } catch (e) {
      message.error('修改失败');
    }
  };

  /** 批量修改归属 */
  const batchChangeOwnership = async (ownership: number) => {
    const items = selectedRowKeys.map(key => ({ domain: String(key), belong: ownership }));
    try {
      await domainManageService.upsertDomainTags({
        productId: getProductId(),
        items,
      });
      message.success('批量修改成功');
      setSelectedRowKeys([]);
      refreshTable();
    } catch (e) {
      message.error('批量修改失败');
    }
  };

  /** 新增域名（调用真实接口） */
  const handleAddDomain = async (domain: string, belong: number) => {
    try {
      await domainManageService.upsertDomainTags({
        productId: getProductId(),
        items: [{ domain, belong }],
      });
      message.success('添加成功');
      // 刷新列表
      refreshTable();
    } catch (e) {
      message.error('添加失败');
    }
  };
  const filteredData = displayData.filter((item) => {
    if (searchText && !(item.domain ?? '').toLowerCase().includes(searchText.toLowerCase())) return false;
    if (Number(activeTab) !== DomainBelongType.DOMAIN_BELONG_ALL) {
      const tabType = Number(activeTab);
      if (item.ownership !== tabType) return false;
    }
    return true;
  });

  /** 搜索栏接入搜索过滤 */
  const columns = [
    {
      title: '域名',
      dataIndex: 'domain',
    },
    {
      title: '请求量',
      dataIndex: 'count',
      width: 100,
    },
    {
      title: '归属类型',
      dataIndex: 'ownership',
      width: 240,
      render: (_: number, record: DomainRecord) => (
        <OwnershipSelector
          value={record.ownership}
          onChange={type => changeOwnership(record.id, type)}
        />
      ),
    },
  ];

  return (
    <Drawer
      title={<div>
        <div>管理域名归属</div>
        <div className={styles.drawerSubtitle}>标记「自身 / 第三方」，可搜索后批量调整，修改即时生效、可随时回退</div>
      </div>}
      width={720}
      visible={visible}
      onClose={onClose}
      className="domain-ownership-drawer"
      bodyStyle={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '16px' }}
    >
      {/* 统计卡片 */}
      <div className="flex gap-[12px] mb-[16px] flex-shrink-0">
        {[
          DomainBelongType.DOMAIN_BELONG_ALL,
          DomainBelongType.DOMAIN_BELONG_SELF,
          DomainBelongType.DOMAIN_BELONG_THIRD,
          DomainBelongType.DOMAIN_BELONG_UNCLASSIFIED,
        ].map((type) => {
          const item = { type, label: OWNERSHIP_LABEL[type], value: statsMap[type] };
          const { color, bg } = OWNERSHIP_COLOR[item.type];
          const isActive = activeTab === String(item.type);
          return (
            <div
              key={String(item.type)}
              className={styles.statCard}
              style={{
                borderColor: isActive ? color : `${color}26`,
                backgroundColor: bg,
              }}
              onClick={() => setActiveTab(String(item.type))}
            >
              <span className={styles.statLabel} style={{ color }}>{item.label}</span>
              <span className={styles.statValue} style={{ color }}>{item.value}</span>
            </div>
          );
        })}
      </div>

      {/* 搜索栏 */}
      <div className="flex justify-between mb-[16px] flex-shrink-0">
        <Input.Search
          placeholder="搜索域名"
          style={{ width: 300 }}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          onSearch={setSearchText}
        />
        <Button type="primary" onClick={() => setAddModalVisible(true)}>新增域名</Button>
      </div>

      {/* 归属分类 Tab */}
      <div className={styles.ownershipTabBar}>
        {[
          DomainBelongType.DOMAIN_BELONG_ALL,
          DomainBelongType.DOMAIN_BELONG_SELF,
          DomainBelongType.DOMAIN_BELONG_THIRD,
          DomainBelongType.DOMAIN_BELONG_UNCLASSIFIED,
        ].map((type) => {
          const isActive = activeTab === String(type);
          return (
            <button
              key={String(type)}
              type="button"
              className={`${styles.ownershipTabItem}${isActive ? ` ${styles.ownershipTabItemActive}` : ''}`}
              onClick={() => setActiveTab(String(type))}
            >
              {OWNERSHIP_LABEL[type]}
            </button>
          );
        })}
      </div>

      {/* 批量操作条 */}
      {selectedRowKeys.length > 0 && (
        <div className="flex items-center gap-[12px] mb-[12px] p-[8px_12px] bg-[#e6f7ff] rounded flex-shrink-0">
          <span>已选 {selectedRowKeys.length} 个域名，批量标记为：</span>
          <OwnershipSelector value={DomainBelongType.DOMAIN_BELONG_SELF} onChange={batchChangeOwnership} />
        </div>
      )}

      {/* 表格：自适应剩余高度，内部滚动 */}
      <div className={styles.tableWrapper}>
        <Table<DomainRecord>
          rowKey="id"
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          pagination={false}
          rowSelection={{
            selectedRowKeys,
            onChange: keys => setSelectedRowKeys(keys),
          }}
        />
      </div>
      <AddDomainModal
        visible={addModalVisible}
        onClose={() => setAddModalVisible(false)}
        onOk={handleAddDomain}
      />
    </Drawer>
  );
};

export const DomainOwnershipButton = (props: { refreshExternalTable: () => void }) => {
  const [drawerVisible, setDrawerVisible] = useState(false);

  return <>
    <Button icon={<AimOutlined />} onClick={() => setDrawerVisible(true)}>
      域名归属
    </Button>
    <DomainOwnershipDrawer
      visible={drawerVisible}
      onClose={() => setDrawerVisible(false)}
      refreshExternalTable={props.refreshExternalTable}
    />
  </>;
};
